import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
    MessageBarBody,
    MessageBarTitle
} from '@fluentui/react-components';

// Component functions
import * as ComponentFunctions from './FileUploadForm.Functions';

// Components
import { FileSelectionSection } from './FileSelectionSection';
import { SelectedFilesSection } from './SelectedFilesSection';
import { GuideDrawer } from '../../../../theme/components/GuideDrawer';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';

// Models
import { NewFileUpload, FileUploadRequest } from '../../../../models/Entities/Contracts/FileUpload';

// i18n
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

// Styles
import './FileUploadForm.Styles.css';

interface FileUploadFormValidation {
    file: boolean;
}

// Add type for per-file upload status
interface FileUploadStatus {
    [fileName: string]: 'pending' | 'success' | 'error';
}

const getFileKey = (file: File): string => `${file.name}-${file.size}-${file.lastModified}`;

// Helper functions for button text
const getUploadingButtonText = (t: TFunction): string => {
    return t('fileUpload.uploading');
};

const getUpdateButtonText = (t: TFunction): string => {
    return t('fileUpload.update');
};

const getUploadButtonText = (t: TFunction): string => {
    return t('fileUpload.upload');
};

// Helper function to get button text based on state
const getButtonTextForState = (isUploading: boolean, isEditing: boolean, t: TFunction): string => {
    if (isUploading) {
        return getUploadingButtonText(t);
    }
    if (isEditing) {
        return getUpdateButtonText(t);
    }
    return getUploadButtonText(t);
};

// Helper functions for title text
const getEditTitleText = (t: TFunction): string => {
    return t('fileUpload.editFile');
};

const getUploadTitleText = (t: TFunction): string => {
    return t('fileUpload.uploadFile');
};

// Helper function to handle file upload process (updated for per-file status)
const uploadFiles = async (
    selectedFiles: File[],
    formData: NewFileUpload,
    createFileUpload: (data: FileUploadRequest) => Promise<void>,
    setUploadProgress: React.Dispatch<React.SetStateAction<{ [key: string]: number }>>,
    setFileStatus: React.Dispatch<React.SetStateAction<FileUploadStatus>>
) => {
    for (const file of selectedFiles) {
        const fileKey = getFileKey(file);
        const fileUploadRequest: FileUploadRequest = {
            file,
            entityType: 'contract',
            entityId: formData.entityId,
            tags: formData.tags,
            metadata: formData.metadata,
            onUploadProgress: (percent) => {
                setUploadProgress(prev => ({ ...prev, [fileKey]: percent }));
            }
        };
        setFileStatus(prev => ({ ...prev, [fileKey]: 'pending' }));
        setUploadProgress(prev => ({ ...prev, [fileKey]: 0 }));
        try {
            await createFileUpload(fileUploadRequest);
            setFileStatus(prev => ({ ...prev, [fileKey]: 'success' }));
        } catch {
            setFileStatus(prev => ({ ...prev, [fileKey]: 'error' }));
        }
    }
};

const FileUploadFormComponent = (props: ComponentFunctions.Props) => {
    const { t } = useTranslation();

    // Form state
    const [submittedForm, setSubmittedForm] = useState<boolean>(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
    const [isUploading, setIsUploading] = useState<boolean>(false);
    // Add per-file status state
    const [fileStatus, setFileStatus] = useState<FileUploadStatus>({});
    // Show success message before closing
    const [showSuccess, setShowSuccess] = useState<boolean>(false);
    
    // Form data
    const [formData, setFormData] = useState<NewFileUpload>({
        tags: [],
        metadata: {},
        entityId: props.ContractId
    });
    
    // Form validation
    const [formValidation, setFormValidation] = useState<FileUploadFormValidation>({
        file: true
    });
    
    // Effects
    useEffect(() => {
        // Elimina el cierre automático al finalizar la subida
    }, [props.FileUploadResult, submittedForm, props]);

    useEffect(() => {
        if (props.EditingFileUpload && props.isOpen) {
            setFormData({
                tags: props.EditingFileUpload.tags ?? [],
                metadata: props.EditingFileUpload.metadata ?? {},
                entityId: props.ContractId
            });
        }
    }, [props.EditingFileUpload, props.isOpen, props.ContractId]);

    // Handlers
    const handleFormReset = () => {
        setFormData({
            tags: [],
            metadata: {},
            entityId: props.ContractId
        });
        setSelectedFiles([]);
        setUploadProgress({});
        setFileStatus({});
        setShowSuccess(false);
        setSubmittedForm(false);
        setIsUploading(false);
        setFormValidation({
            file: true
        });
    };

    const handleFileSelection = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        if (files.length > 0) {
            setSelectedFiles(prev => {
                // Keep previous selection and append only new unique files.
                const existing = new Set(prev.map(f => `${f.name}-${f.size}-${f.lastModified}`));
                const toAdd = files.filter(f => !existing.has(`${f.name}-${f.size}-${f.lastModified}`));
                return [...prev, ...toAdd];
            });
            setFormValidation(prev => ({ ...prev, file: true }));
        }
        // Allow re-selecting the same file(s) in a later interaction.
        event.target.value = '';
    }, []);

    const handleRemoveFile = useCallback((index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    }, []);

    const handleInputChange = useCallback((field: keyof NewFileUpload, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    const validateForm = (): boolean => {
        const validation: FileUploadFormValidation = {
            file: !props.EditingFileUpload ? selectedFiles.length > 0 : true,
        };

        setFormValidation(validation);
        return Object.values(validation).every(Boolean);
    };

    const handleSubmit = useCallback(async () => {
        if (!validateForm()) {
            setSubmittedForm(true);
            return;
        }

        setSubmittedForm(true);
        setIsUploading(true);
        setUploadProgress({});
        setFileStatus({});
        try {
            // Upload new files
            await uploadFiles(selectedFiles, formData, props.CreateFileUpload, setUploadProgress, setFileStatus);
            
        } catch (error) {
            console.error('Upload failed:', error);
            // Show error message using MessageComponent
            setFormValidation(prev => ({
                ...prev,
                file: selectedFiles.length > 0
            }));
        } finally {
            setIsUploading(false);
        }
    }, [formData, selectedFiles, props.EditingFileUpload, props.CreateFileUpload]);

    // Remove old auto-close effect
    // Add new effect to close only after all files succeed
    useEffect(() => {
        if (
            submittedForm &&
            selectedFiles.length > 0 &&
            selectedFiles.every(f => fileStatus[getFileKey(f)] === 'success') &&
            Object.keys(fileStatus).length === selectedFiles.length
        ) {
            setShowSuccess(true);
            // Auto-close after short delay
            const timeout = setTimeout(() => {
                handleFormReset();
                setShowSuccess(false);
                props.onDismiss();
            }, 1200);
            return () => clearTimeout(timeout);
        }
    }, [fileStatus, submittedForm, selectedFiles, props.onDismiss]);

    const handleDismiss = useCallback(() => {
        if (isUploading) return;
        handleFormReset();
        props.onDismiss();
    }, [isUploading, props.onDismiss]);

    return (
        <GuideDrawer
            open={props.isOpen}
            size="large"
            title={props.EditingFileUpload ? getEditTitleText(t) : getUploadTitleText(t)}
            onDismiss={handleDismiss}
            preventDismiss={isUploading}
            closeLabel={t('fileUpload.close')}
            testId="file-upload-drawer"
            secondaryAction={{ label: t('fileUpload.cancel') }}
            primaryAction={{
                label: getButtonTextForState(isUploading, !!props.EditingFileUpload, t),
                onClick: handleSubmit,
                loading: isUploading
            }}
        >
            {props.FileUploadResult && !props.FileUploadResult.succeeded && (
                <GuideMessageBar intent="error" layout="multiline">
                    <MessageBarBody>
                        <MessageBarTitle>{props.FileUploadResult.message}</MessageBarTitle>
                    </MessageBarBody>
                </GuideMessageBar>
            )}

            {!props.EditingFileUpload && (
                <FileSelectionSection
                    formValidation={formValidation}
                    submittedForm={submittedForm}
                    isUploading={isUploading}
                    handleFileSelection={handleFileSelection}
                    t={t}
                />
            )}

            {selectedFiles.length > 0 && (
                <SelectedFilesSection
                    selectedFiles={selectedFiles}
                    uploadProgress={uploadProgress}
                    isUploading={isUploading}
                    handleRemoveFile={handleRemoveFile}
                    t={t}
                    fileStatus={fileStatus} // Pass per-file status for display
                />
            )}

            {isUploading && (
                <GuideMessageBar intent="info">
                    <MessageBarBody>
                        <MessageBarTitle>{t('fileUpload.uploading')}</MessageBarTitle>
                        {t('fileUpload.uploadingFiles')}
                    </MessageBarBody>
                </GuideMessageBar>
            )}
            {showSuccess && (
                <GuideMessageBar intent="success">
                    <MessageBarBody>
                        <MessageBarTitle>{t('fileUpload.uploadSuccess')}</MessageBarTitle>
                        {t('fileUpload.allFilesUploaded')}
                    </MessageBarBody>
                </GuideMessageBar>
            )}
        </GuideDrawer>
    );
};

export { FileUploadFormComponent };

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(FileUploadFormComponent as any);
